import { motion, useInView } from "motion/react";
import React, { useRef } from "react";
import { HorizontalScroll } from "./HorizontalScroll";
import type { ProcessData } from "../../utils/mockProjects";
import stringToParagraphs from "../../utils/splitPara";

interface ProcessProps {
  processData: ProcessData;
  galleryImages: string[];
  bgColor: string;
}
export const Process: React.FC<ProcessProps> = ({
  processData,
  galleryImages,
  bgColor,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  return (
    <div
      className={`w-full flex flex-col items-center relative justify-between gap-12 mt-4`}
    >
      <div className="w-full flex lg:flex-row flex-col items-start justify-between gap-24">
        <div className="lg:w-1/2 flex flex-col items-start justify-center lg:border-l-[1px] text-primary font-gilroy-bold lg:pl-8 gap-8">
          <p className="lg:text-2xl md:text-base text-sm">Process</p>
          <p className="w-full lg:text-5xl md:text-3xl text-lg lg:leading-[1]">
            {processData.heading}
          </p>
        </div>

        <div className="lg:w-2/3 flex flex-col text-justify gap-8 items-center justify-center lg:text-2xl md:text-lg text-base font-gilroy-regular">
          {stringToParagraphs(processData.subheading)}
        </div>
      </div>

      {window.innerWidth > 1024 ? (
        <HorizontalScroll processSteps={processData.steps as string[]} />
      ) : (
        <div className="w-full flex flex-col items-center gap-6">
          <p className="w-full font-gilroy-bold md:text-xl text-lg">Steps</p>
          <div className="w-screen px-8 h-fit flex overflow-auto gap-4">
            {processData.steps.map((step, index) => (
              <div
                style={{
                  backgroundImage: processData.bgGradient,
                }}
                className="font-gilroy-regular flex-col flex gap-6 min-w-[210px] p-4 text-base"
                key={index}
              >
                <span className="font-gilroy-bold text-2xl">{index + 1}</span>
                {step}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="w-screen lg:flex hidden relative" ref={ref}>
        <div
          style={{ backgroundColor: bgColor }}
          className="w-full absolute h-[200vh] bottom-1/2 -z-20"
        />
        {processData.processImages.map((image, index) => (
          <motion.img
            key={index}
            src={image}
            alt={`Process step ${index + 1}`}
            className="lg:w-1/5 w-[25vw] aspect-square object-cover rounded-[20px]"
            initial={{ y: index % 2 === 0 ? -100 : 100, opacity: 0 }}
            animate={isInView && { y: index % 2 === 0 ? -20 : 20, opacity: 1 }}
            transition={{ duration: 0.5, delay: index * 0.2 }}
          />
        ))}
      </div>

      <div className="w-screen lg:hidden grid lg:grid-cols-3 grid-cols-2 relative mt-8">
        <div
          style={{ backgroundColor: bgColor }}
          className="w-full absolute h-[200vh] bottom-1/2 -z-20"
        />
        {galleryImages
          .filter((media) => media.endsWith(".webp"))
          .slice(0, 4)
          .map((media, index) => (
            <div
              className={`grid-cols-1 lg:w-[33.2vw] flex items-center justify-center overflow-hidden`}
              key={index}
            >
              <img
                key={index}
                src={media}
                alt={`Gallery image ${index + 1}`}
                className="object-cover w-full h-full block"
              />
            </div>
          ))}
      </div>
    </div>
  );
};
